import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
    Box, Paper, Typography, TextField, Button, 
    Grid, CircularProgress, Alert, IconButton
} from '@mui/material';
import { ArrowBack, Save } from '@mui/icons-material';
import api from '../../api/axios';

const ClientForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = !!id;

    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(isEditMode);
    const [error, setError] = useState(null);
    
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone_number: '',
        address: '',
        notes: ''
    });

    useEffect(() => {
        if (isEditMode) {
            loadClient();
        }
    }, [id]);

    const loadClient = async () => {
        try {
            const res = await api.get(`/admin/clients/${id}`);
            const data = res.data;
            setFormData({
                name: data.name || '',
                email: data.email || '',
                phone_number: data.phone_number || '',
                address: data.address || '',
                notes: data.notes || ''
            });
        } catch (err) {
            console.error('Failed to load client:', err);
            setError('Failed to load client details.');
        }
        setInitialLoading(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            if (isEditMode) {
                await api.put(`/admin/clients/${id}`, formData);
            } else {
                await api.post('/admin/clients', formData);
            }
            navigate(isEditMode ? `/clients/${id}` : '/clients');
        } catch (err) {
            console.error('Failed to save client:', err);
            setError(err.response?.data?.detail || 'Failed to save client. Please check the inputs.');
        }
        setLoading(false);
    };

    if (initialLoading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
    }

    return (
        <Box sx={{ pb: 8, maxWidth: 800, mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
                    <ArrowBack />
                </IconButton>
                <Typography variant="h5" fontWeight={700}>
                    {isEditMode ? 'Edit Client' : 'New Client'}
                </Typography>
            </Box>

            <Paper elevation={0} sx={{ p: 4, border: '1px solid #E2E8F0', borderRadius: 3 }}>
                {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

                <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                BASIC INFORMATION
                            </Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Client Name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                placeholder="e.g. John Doe, Acme Corp"
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Email Address"
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="contact@example.com"
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Phone Number"
                                name="phone_number"
                                value={formData.phone_number}
                                onChange={handleChange}
                                placeholder="+61 400 000 000"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, mt: 2 }}>
                                ADDRESS & NOTES
                            </Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Billing/Home Address"
                                name="address"
                                value={formData.address}
                                onChange={handleChange}
                                multiline
                                rows={2}
                                placeholder="123 Main St, City, State"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Notes"
                                name="notes"
                                value={formData.notes}
                                onChange={handleChange}
                                multiline
                                rows={4}
                                placeholder="Gate codes, access details, preferences..."
                            />
                        </Grid>

                        <Grid item xs={12} sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                            <Button 
                                variant="outlined" 
                                onClick={() => navigate(-1)}
                                disabled={loading}
                            >
                                Cancel
                            </Button>
                            <Button 
                                type="submit" 
                                variant="contained" 
                                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Save />}
                                disabled={loading}
                            >
                                {isEditMode ? 'Update Client' : 'Create Client'}
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </Paper>
        </Box>
    );
};

export default ClientForm;
